import { useLocalStorage } from 'usehooks-ts';
import { Address } from 'viem';
import { localStorageKeys } from '../lib/localstorageKeys';

type SubaccountSigningData =
  | {
      signerKey: Address;
      signingMethod: 'one-click';
      linked: boolean;
    }
  | { signingMethod: 'manual' };

export type SubaccountData = SubaccountSigningData;

interface WalletData {
  [address: string]: {
    subaccounts: {
      [subaccountId: string]: SubaccountData;
    };
  };
}

// Custom hook for managing wallet and subaccount data
export function useWalletStorage() {
  const [walletData, setWalletData] = useLocalStorage<WalletData>(localStorageKeys.walletData, {});

  const updateSubaccountData = (address: string, subaccountId: string, data: SubaccountData) => {
    setWalletData((prevData) => ({
      ...prevData,
      [address]: {
        ...prevData[address],
        subaccounts: {
          ...prevData[address]?.subaccounts,
          [subaccountId]: data,
        },
      },
    }));
  };

  const getSubaccountData = (
    address: string | undefined,
    subaccountId: string | undefined
  ): SubaccountData | undefined => {
    if (!address || !subaccountId) return undefined;
    return walletData[address]?.subaccounts[subaccountId];
  };

  const getSigningMethod = (
    address?: string,
    subaccountId?: string
  ): 'one-click' | 'manual' | undefined => {
    if (!address || !subaccountId) return undefined;
    return walletData[address]?.subaccounts[subaccountId]?.signingMethod;
  };

  const removeSubaccount = (address: string, subaccountId: string) => {
    setWalletData((prevData) => {
      const newData = structuredClone(prevData);
      if (newData[address]?.subaccounts[subaccountId]) {
        delete newData[address].subaccounts[subaccountId];
      }
      return newData;
    });
  };

  // Function to clear all data for a specific wallet
  const clearWallet = (address: string) => {
    setWalletData((prevData) => {
      const newData = structuredClone(prevData);
      delete newData[address];
      return newData;
    });
  };

  return {
    walletData,
    updateSubaccountData,
    getSubaccountData,
    getSigningMethod,
    removeSubaccount,
    clearWallet,
  };
}
